import { useMutation, useQuery } from '@tanstack/react-query';

import { queryKeys, usePatronSession } from '@app/hooks';
import { makeRequest } from '@app/utils';
import {
  ShapeWithTotalRecords,
  SortDirection,
  SortDirectionReversed,
} from '@app/constants';

export type Project = ShapeWithTotalRecords<{
  id: string;
  name: string;
  tags: string[];
  savedInstances: unknown[];
  projectSearches: unknown[];
}>;

export interface ProjectsDto<T = Project> {
  projects: T[];
}

export interface ProjectCreationDto {
  name: string;
  description?: string;
}

export interface AddInstanceToProjectPayloadDto {
  projectId: string;
  instanceIds: { instanceId: string }[];
}

const createUrl = (externalSystemId: string) =>
  `/opac-patron/account/${externalSystemId}/projects`;

interface ProjectsFetchProps {
  search?: string;
  sortDirection?: SortDirection;
}

export const useProjectsFetch = ({
  search: searchQuery,
  sortDirection = SortDirection.desc,
}: ProjectsFetchProps = {}) => {
  const { externalSystemId } = usePatronSession();
  const url = createUrl(externalSystemId as string);

  return useQuery<ProjectsDto, unknown, Project[]>(
    [queryKeys.projects, externalSystemId, searchQuery, sortDirection],
    () =>
      makeRequest<ProjectsDto>({
        url,
        params: {
          search: searchQuery,
          sort: SortDirectionReversed[sortDirection],
        },
      }),
    {
      enabled: Boolean(externalSystemId),
      select: ({ projects }) =>
        projects.map(project => ({
          ...project,
        })),
    }
  );
};

export const useCreateProjectsMutation = () => {
  const { externalSystemId } = usePatronSession();
  const url = createUrl(externalSystemId as string);

  return useMutation((data: ProjectCreationDto) =>
    makeRequest({
      url,
      method: 'POST',
      data,
    })
  );
};

export const useEditProjectsMutation = ({ id }) => {
  const { externalSystemId } = usePatronSession();
  const url = `${createUrl(externalSystemId as string)}/${id}`;

  return useMutation((data: ProjectCreationDto) =>
    makeRequest({
      url,
      method: 'PUT',
      data,
    })
  );
};

export const useAddInstancesToProjectMutation = () => {
  const { externalSystemId } = usePatronSession();

  return useMutation<
    unknown,
    { response: { data: { errorMessage: string } } },
    AddInstanceToProjectPayloadDto
  >(({ instanceIds, projectId }) =>
    makeRequest({
      url: `${createUrl(
        externalSystemId as string
      )}/${projectId}/instance-actions`,
      method: 'POST',
      data: instanceIds,
    })
  );
};
